import { useEffect, useMemo, useState } from "react";
import { useMediaQuery } from "react-responsive";
import { useClient } from "../../context/ClientContext";
import { api } from "../../services/api";
import { resolveImgUrl } from "../../templates/4/utils";
import { RichContent } from "../../shared/components/RichContent";

const norm = (s = "") => s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
const splitKeywords = (s = "") => s.split(",").map(k => k.trim()).filter(Boolean);

export const Faq = () => {
  const { theme, config } = useClient();
  const isMobile = useMediaQuery({ query: "(max-width: 700px)" });
  const [selectedTag, setSelectedTag] = useState(null);
  const [openIdx, setOpenIdx] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const t4 = config?.uiConfig?.t4 || {};
  const brand = t4.brand || {};
  const page = t4.faqPage || {};
  const font = brand.font || "system-ui, sans-serif";
  const primary = theme.primaryColor || brand.colors?.primary || "#4B72F5";
  const textCol = theme.textColor || brand.colors?.text || "#1a1a3a";
  const questionColor = page.questionColor || textCol;
  const answerColor = page.answerColor || textCol;
  const background = page.bgColor || theme.backgroundColor || "#ffffff";
  const align = page.titleAlign || "center";
  const showTags = page.showSearch !== false;

  useEffect(() => {
    (async () => {
      setLoading(true);
      const res = await api.faq.getFaqs();
      setItems(res.status === "success" ? (res.data || []) : []);
      setLoading(false);
    })();
  }, []);

  const tags = useMemo(() => {
    const seen = new Map();
    items.forEach(it => splitKeywords(it.keywords).forEach(k => {
      const key = norm(k);
      if (!seen.has(key)) seen.set(key, k);
    }));
    return [...seen.values()];
  }, [items]);

  const filtered = useMemo(() => {
    if (!selectedTag) return items;
    const target = norm(selectedTag);
    return items.filter(it => splitKeywords(it.keywords).some(k => norm(k) === target));
  }, [items, selectedTag]);

  // Se muestra solo si el dueño la activó en el diseño de la tienda.
  if (page.visible !== true) {
    return (
      <div style={{ minHeight: "50vh", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: font }}>
        <div style={{ color: textCol, opacity: 0.6, fontSize: 15 }}>Esta página no está disponible.</div>
      </div>
    );
  }

  return (
    <div style={{ background, minHeight: "60vh", padding: isMobile ? "24px 16px 40px" : "36px 32px 64px", fontFamily: font }}>
      <div style={{ maxWidth: 760, margin: "0 auto" }}>
        {page.title && (
          <h1 style={{
            fontWeight: 800,
            fontSize: page.titleStyle?.fontSize || (isMobile ? 26 : 40),
            fontFamily: page.titleStyle?.fontFamily || font,
            color: page.titleStyle?.color || textCol,
            textAlign: align,
            margin: "0 0 10px",
          }}>{page.title}</h1>
        )}
        <div style={{ width: 40, height: 4, background: primary, borderRadius: 2,
          margin: align === "center" ? "0 auto 16px" : align === "right" ? "0 0 16px auto" : "0 0 16px" }} />
        {page.subtitle && (
          <p style={{ fontSize: isMobile ? 14 : 16, color: textCol, opacity: 0.65, textAlign: align, margin: "0 0 40px" }}>
            {page.subtitle}
          </p>
        )}

        {showTags && tags.length > 0 && (
          <div style={{
            display: "flex", flexWrap: "wrap", gap: 8, marginTop: page.subtitle ? 0 : 40, marginBottom: isMobile ? 28 : 36,
            justifyContent: align === "center" ? "center" : align === "right" ? "flex-end" : "flex-start",
          }}>
            {[{ label: "Todas", value: null }, ...tags.map(t => ({ label: t, value: t }))].map(({ label, value }) => {
              const active = selectedTag === value;
              return (
                <button
                  key={label}
                  onClick={() => setSelectedTag(value)}
                  style={{
                    padding: "8px 18px", borderRadius: 999, fontSize: 13, fontWeight: 600,
                    fontFamily: font, cursor: "pointer", whiteSpace: "nowrap",
                    border: `1.5px solid ${active ? textCol : "rgba(0,0,0,0.15)"}`,
                    background: active ? textCol : "transparent",
                    color: active ? "#fff" : textCol,
                    transition: "all 0.15s",
                  }}
                >
                  {label}
                </button>
              );
            })}
          </div>
        )}

        {loading && (
          <>
            <style>{`
              @keyframes faq-shimmer {
                0% { background-position: -600px 0; }
                100% { background-position: 600px 0; }
              }
              .faq-sk {
                background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
                background-size: 600px 100%;
                animation: faq-shimmer 1.4s infinite linear;
                border-radius: 6px;
              }
            `}</style>
            <div style={{ background: "#fff" }}>
              {[100, 70, 85, 60, 90].map((w, i) => (
                <div key={i} style={{
                  display: "flex", alignItems: "center", gap: 14,
                  padding: isMobile ? "14px 16px" : "18px 24px",
                  borderBottom: i < 4 ? "1px solid rgba(0,0,0,0.06)" : "none",
                }}>
                  <div className="faq-sk" style={{ width: 44, height: 44, borderRadius: 10, flexShrink: 0 }} />
                  <div className="faq-sk" style={{ height: 16, width: `${w}%`, maxWidth: isMobile ? `${w * 0.7}%` : `${w}%` }} />
                </div>
              ))}
            </div>
          </>
        )}

        {!loading && items.length === 0 && (
          <div style={{ textAlign: "center", padding: "40px 0", color: textCol, opacity: 0.5, fontSize: 14 }}>
            Todavía no hay preguntas frecuentes cargadas.
          </div>
        )}

        {!loading && items.length > 0 && filtered.length === 0 && (
          <div style={{ textAlign: "center", padding: "40px 0", color: textCol, opacity: 0.5, fontSize: 14 }}>
            No encontramos preguntas para "{selectedTag}".
          </div>
        )}

        {!loading && filtered.length > 0 && (
          <div style={{ background: "#fff" }}>
            {filtered.map((item, idx) => {
              const isOpen = openIdx === idx;
              const isLast = idx === filtered.length - 1;
              return (
                <div key={item.id ?? idx} style={{ borderBottom: isLast ? "none" : "1px solid rgba(0,0,0,0.06)" }}>
                  <button
                    onClick={() => setOpenIdx(isOpen ? null : idx)}
                    style={{
                      width: "100%", display: "flex", alignItems: "center", gap: 14,
                      padding: isMobile ? "14px 16px" : "18px 24px", background: "none", border: "none",
                      cursor: "pointer", textAlign: "left", fontFamily: font,
                    }}>
                    <div style={{
                      width: 44, height: 44, borderRadius: 10, flexShrink: 0,
                      border: "1.5px solid rgba(0,0,0,0.1)", overflow: "hidden",
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                      {item.imagen ? (
                        <img src={resolveImgUrl(item.imagen)} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      ) : (
                        <span style={{ fontSize: 20 }}>{item.icono || "❓"}</span>
                      )}
                    </div>
                    <span style={{ flex: 1, fontWeight: 700, fontSize: isMobile ? 14 : 16, color: questionColor }}>{item.pregunta}</span>
                    <span style={{
                      color: textCol, opacity: 0.5, fontSize: 14, flexShrink: 0, transition: "transform 0.2s",
                      transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                    }}>▾</span>
                  </button>
                  {isOpen && item.respuesta && (
                    <RichContent
                      html={item.respuesta}
                      linkColor={primary}
                      style={{
                        padding: isMobile ? "0 16px 18px 74px" : "0 24px 22px 82px",
                        fontSize: 13, color: answerColor, opacity: page.answerColor ? 1 : 0.7, lineHeight: 1.6,
                      }}
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
